// frontend/src/utils/recurringMatch.js
// Mirror of backend recurringPaymentService.isSameMerchant + ±tolerance band check.
// Used to compute a transaction row's recurring status from PlaidContext sets without
// a per-row API call. Keep this in sync with backend/services/recurringPaymentService.js.

export function isSameMerchant(name1, name2) {
  if (!name1 || !name2) return false;

  const n1 = String(name1).toLowerCase().trim();
  const n2 = String(name2).toLowerCase().trim();

  if (n1 === n2) return true;

  const getFirstWord = (str) => {
    const cleaned = str.replace(/\s+(inc|llc|ltd|corp|co|payment|auto|recurring)/gi, '').trim();
    return cleaned.split(/\s+/)[0];
  };

  const word1 = getFirstWord(n1);
  const word2 = getFirstWord(n2);

  if (word1.length > 3 && word1 === word2) return true;
  if (n1.includes(word2) || n2.includes(word1)) return true;

  return false;
}

export function isWithinTolerance(amount, anchor, tolerance = 0.10) {
  if (!anchor) return false;
  const lower = anchor * (1 - tolerance);
  const upper = anchor * (1 + tolerance);
  return amount >= lower && amount <= upper;
}

// Returns one of: 'detected' | 'manual' | 'excluded' | 'none'
// recurringList is the array returned by GET /api/recurring-expenses (each entry
// carries `name`, `amountAnchor`, `tolerance`, `source`).
// excludedMerchants is the array returned by GET /api/recurring-expenses/excluded.
export function getRecurringStatus(transaction, recurringList, excludedMerchants) {
  if (!transaction) return 'none';
  const txnName = transaction.name || transaction.merchantName;
  const txnAmount = Math.abs(transaction.amount ?? 0);
  if (!txnName) return 'none';

  // Check if a recurring entry covers this transaction
  for (const entry of recurringList || []) {
    const anchor = entry.amountAnchor ?? entry.amount;
    const tolerance = entry.tolerance ?? 0.10;
    if (
      isSameMerchant(txnName, entry.name) &&
      isWithinTolerance(txnAmount, anchor, tolerance)
    ) {
      return entry.source === 'manual' ? 'manual' : 'detected';
    }
  }

  // Not in recurring list — check if the merchant is in the user's exclusion list.
  // Excluded entries don't carry an amount band on the wire (they're identified by
  // merchant only), so we mark the row as excluded if the merchant matches.
  for (const merchantName of excludedMerchants || []) {
    if (isSameMerchant(txnName, merchantName)) {
      return 'excluded';
    }
  }

  return 'none';
}
